import React, { useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import {
  Upload,
  Mic,
  FileAudio,
  Copy,
  Download,
  Loader2,
  Check,
  AlertCircle,
  Trash2,
} from 'lucide-react'

interface TranscriptionResult {
  text: string
  duration?: number
  language?: string
  segments?: Array<{
    start: number
    end: number
    text: string
  }>
}

export function AudioScribeTool() {
  const { user } = useAuth()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [language, setLanguage] = useState('auto')
  const [format, setFormat] = useState('text')
  const [processing, setProcessing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<TranscriptionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/m4a', 'audio/ogg', 'audio/mp4']
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|ogg)$/i)) {
        setError('Please select a valid audio file (MP3, WAV, M4A, or OGG)')
        return
      }
      setSelectedFile(file)
      setResult(null)
      setError(null)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('audio/')) {
      setSelectedFile(file)
      setResult(null)
      setError(null)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const transcribeAudio = async () => {
    if (!selectedFile || !user) return

    setProcessing(true)
    setUploading(true)
    setError(null)
    setUploadProgress(0)

    try {
      // Upload file to temp-audio bucket
      const fileName = user.id + '/' + Date.now() + '-' + selectedFile.name
      const { error: uploadError } = await supabase.storage
        .from('temp-audio')
        .upload(fileName, selectedFile)

      if (uploadError) throw uploadError

      setUploading(false)
      setUploadProgress(100)

      // Call edge function to transcribe
      const { data: functionData, error: functionError } = await supabase.functions.invoke(
        'transcribe-audio',
        {
          body: {
            file_path: fileName,
            options: {
              language: language,
              format: format === 'text' ? 'verbose_json' : format,
            },
          },
        }
      )

      if (functionError) throw functionError

      if (functionData.error) {
        throw new Error(functionData.message || functionData.error)
      }

      setResult(functionData.transcription)
    } catch (err: any) {
      console.error('Transcription error:', err)
      setError(err.message || 'An error occurred during transcription')
    } finally {
      setProcessing(false)
      setUploading(false)
    }
  }

  const copyToClipboard = async () => {
    if (result?.text) {
      await navigator.clipboard.writeText(result.text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const downloadTranscription = (downloadFormat: string) => {
    if (!result) return

    let content = ''
    let filename = ''
    let mimeType = ''

    switch (downloadFormat) {
      case 'txt':
        content = result.text
        filename = 'transcription-' + Date.now() + '.txt'
        mimeType = 'text/plain'
        break
      case 'srt':
        content = convertToSRT(result.segments || [])
        filename = 'transcription-' + Date.now() + '.srt'
        mimeType = 'text/plain'
        break
      case 'json':
        content = JSON.stringify(result, null, 2)
        filename = 'transcription-' + Date.now() + '.json'
        mimeType = 'application/json'
        break
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const convertToSRT = (segments: Array<{ start: number; end: number; text: string }>): string => {
    return segments
      .map((seg, i) => {
        const startTime = formatSRTTime(seg.start)
        const endTime = formatSRTTime(seg.end)
        return i + 1 + '\n' + startTime + ' --> ' + endTime + '\n' + seg.text + '\n'
      })
      .join('\n')
  }

  const formatSRTTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 1000)
    return (
      String(hours).padStart(2, '0') +
      ':' +
      String(minutes).padStart(2, '0') +
      ':' +
      String(secs).padStart(2, '0') +
      ',' +
      String(ms).padStart(3, '0')
    )
  }

  const clearFile = () => {
    setSelectedFile(null)
    setResult(null)
    setError(null)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Audio Scribe</h1>
        <p className="text-slate-600">
          Transcribe audio files with high accuracy using Whisper Large V3
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Upload & Settings */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileAudio className="h-5 w-5" />
            Upload Audio
          </h2>

          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer mb-6"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">Click to upload or drag and drop</p>
            <p className="text-sm text-slate-400">MP3, WAV, M4A, OGG up to 25MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.ogg"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {selectedFile && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mic className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-800">{selectedFile.name}</p>
                    <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <button
                  onClick={clearFile}
                  className="text-slate-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Settings */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Language</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="auto">Auto-Detect</option>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="pt">Portuguese</option>
                <option value="zh">Chinese</option>
                <option value="ja">Japanese</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Output Format</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="text"
                    checked={format === 'text'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-slate-700">Text Only</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="srt"
                    checked={format === 'srt'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-slate-700">SRT Subtitles</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="vtt"
                    checked={format === 'vtt'}
                    onChange={(e) => setFormat(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-slate-700">VTT Subtitles</span>
                </label>
              </div>
            </div>
          </div>

          <Button onClick={transcribeAudio} disabled={!selectedFile || processing} className="w-full">
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {uploading ? 'Uploading...' : 'Transcribing...'}
              </>
            ) : (
              <>
                <Mic className="h-4 w-4 mr-2" />
                Transcribe Audio
              </>
            )}
          </Button>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Right Panel - Results */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileAudio className="h-5 w-5" />
            Transcription
          </h2>

          {result ? (
            <div className="space-y-4">
              {/* Stats */}
              {result.duration && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Duration</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {Math.floor(result.duration / 60)}:{String(Math.floor(result.duration % 60)).padStart(2, '0')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Language</p>
                    <p className="text-lg font-semibold text-slate-800 uppercase">
                      {result.language || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Words</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {result.text.split(/\s+/).length}
                    </p>
                  </div>
                </div>
              )}

              {/* Transcription Text */}
              <div className="border border-slate-200 rounded-lg p-4 bg-white max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono">{result.text}</pre>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button onClick={copyToClipboard} variant="outline" className="flex-1">
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                  <div className="relative flex-1">
                    <select
                      onChange={(e) => downloadTranscription(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Download As...
                      </option>
                      <option value="txt">Text (.txt)</option>
                      <option value="srt">SRT Subtitles (.srt)</option>
                      <option value="json">JSON (.json)</option>
                    </select>
                    <Download className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Mic className="h-16 w-16 mb-4" />
              <p>Upload an audio file and click Transcribe to see results</p>
            </div>
          )}
        </Card>
      </div>

      {/* Tips */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-slate-800 mb-2">Tips for Best Results</h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Use clear audio with minimal background noise</li>
          <li>• Supported formats: MP3, WAV, M4A, OGG</li>
          <li>• Maximum file size: 25MB</li>
          <li>• Supports 50+ languages with auto-detection</li>
          <li>• Best results with single speaker recordings</li>
        </ul>
      </Card>
    </div>
  )
}
